import { useMemo } from 'react';

import ControlPanelContainer from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/ControlPanelContainer';
import { VERIFICATION_TYPE_OPTIONS } from 'dashboard/apps/DashboardLayoutEditor/ControlPanel/Panels/ResourceVerificationPanel';
import CaseDataManagerSchemaSelect from 'dashboard/components/CaseDataManagerSchemaSelect';
import ReactMultiSelect from 'dashboard/components/ReactMultiSelect';
import { generatePlaceHolderDisplay } from 'dashboard/components/ReactMultiSelect/ReactMultiSelect';
import ReactSelect from 'dashboard/components/ReactSelect';
import { VerificationTemplate } from 'dashboard/models/verification-template';
import { ModuleResourceVerificationV2 } from 'dashboard/modules/ResourceVerificationV2';
import Spacer from 'design-system/components/Spacer';
import { Heading, Label } from 'design-system/components/Text';
import { ApiModel } from 'lib/interfaces/api';
import { JsonSchemaObject } from 'lib/json-schema';

export interface ResourceVerificationV2PanelProps {
  module: ModuleResourceVerificationV2;
  dataSchema: JsonSchemaObject;
  publishedVerificationTemplates: VerificationTemplate[];
  updateModule: (module: ModuleResourceVerificationV2) => void;
}

const ResourceVerificationV2Panel = ({
  module,
  dataSchema,
  publishedVerificationTemplates,
  updateModule,
}: ResourceVerificationV2PanelProps) => {
  const handleSetVerificationResourceVariable = (variableToken: string) => {
    updateModule({
      ...module,
      'variable-token': variableToken,
    });
  };

  const handleSetVerificationType = (
    verificationType: ModuleResourceVerificationV2['verification-type'],
  ) => {
    updateModule({
      ...module,
      'verification-type': verificationType,
    });
  };

  const handleSetVerificationTemplates = (
    verificationTemplate: ModuleResourceVerificationV2['verification-templates'],
  ) => {
    updateModule({
      ...module,
      'verification-templates': verificationTemplate,
    });
  };

  const verificationTemplateOptions = useMemo(() => {
    return publishedVerificationTemplates
      .map((verificationTemplate) => {
        return {
          label: verificationTemplate.attributes.name,
          value: verificationTemplate.id,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [publishedVerificationTemplates]);

  return (
    <>
      <ControlPanelContainer>
        <Heading size='sm'>Settings</Heading>
        <Spacer height={2} />

        <Label id='verification-resource-variable-selector-label' size='md' weight='bold'>
          Resource
        </Label>
        <CaseDataManagerSchemaSelect
          jsonSchema={dataSchema}
          inputValue={module['variable-token']}
          onValueChange={handleSetVerificationResourceVariable}
        />

        <Spacer height={2} />

        <Label
          size='md'
          weight='bold'
          id='resource-verification-panel__verification-type-selector--label'
        >
          Type
        </Label>
        <ReactSelect<ApiModel>
          id='resource-verification-panel__verification-type-selector'
          aria-labelledby='resource-verification-panel__verification-type-selector--label'
          value={module['verification-type']}
          options={VERIFICATION_TYPE_OPTIONS}
          onChange={handleSetVerificationType}
          portal={true}
        />

        <Spacer height={2} />

        <Label id='verification-templates-selector-label' size='md' weight='bold'>
          Verification templates
        </Label>
        <ReactMultiSelect
          aria-labelledby='verification-templates-selector-label'
          aria-describedby='verification-templates-selector-label'
          options={verificationTemplateOptions}
          value={module['verification-templates'] || []}
          onChange={handleSetVerificationTemplates}
          placeholderGenerator={generatePlaceHolderDisplay({
            label: { single: 'Verification Template', multiple: 'Verification Templates' },
            emptyIsAll: true,
          })}
          selectAllEnabled
          searchEnabled
          portal
        />
      </ControlPanelContainer>
    </>
  );
};

export default ResourceVerificationV2Panel;
